'use client';

import {
  Box,
  Checkbox,
  CheckboxGroup,
  Flex,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  Skeleton,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { FC, useCallback } from 'react';
import { IFilterObject } from '@/components/Product/Filters/types';
import AllFilters from '@/components/Product/Filters/AllFilters';
import FilterGroups from '@/components/UI/Filter/FilterGroups';
import { filterGroups } from '@/components/Product/Filters/constants';
import FilterBox from '@/components/UI/Filter/FilterBox';
import {
  ANY_VALUE_RANGE,
  VALUE_RANGE_MAX,
  VALUE_RANGE_MIN,
} from '@/constants/product';
import { isEqual } from 'lodash';
import ValueRangeSlider from '@/components/Product/Filters/ValueRangeSlider';
import { IProductCurrency } from '@/types/product';
import ClearFilters from '@/components/Product/Filters/ClearFilters';
import { IAppCategory } from '@/types/api/app/category';

interface DesktopFiltersProps {
  valueRange?: number[];
  onValueRangeChange?: (range: number[]) => void;

  categories?: IAppCategory[];
  categoryOptions?: IAppCategory[];
  onCategoriesChange?: (categories: IAppCategory[]) => void;

  filters?: IFilterObject[];
  onFiltersChange?: (val: IFilterObject[]) => void;

  isLoading?: boolean;
}
const DesktopFilters: FC<DesktopFiltersProps> = ({
  valueRange = ANY_VALUE_RANGE,
  onValueRangeChange,
  filters = [],
  onFiltersChange,

  categories = [],
  categoryOptions,
  onCategoriesChange,

  isLoading,
}) => {
  const allowClear =
    !!filters.length || valueRange !== ANY_VALUE_RANGE || !!categories.length;

  const handleClear = () => {
    onFiltersChange?.([]);
    onCategoriesChange?.([]);
    onValueRangeChange?.(ANY_VALUE_RANGE);
  };

  if (isLoading)
    return (
      <Flex gap="0.5rem" alignItems="center" h="42px" w="100%">
        <Skeleton h="100%" w="46px" />
        <Skeleton h="100%" w="100px" />
        <Skeleton h="100%" w="100px" />
        <Skeleton h="100%" w="100px" />
      </Flex>
    );

  return (
    <Flex
      gap="0.5rem"
      alignItems="center"
      display={{ base: 'none', lg: 'flex' }}
    >
      <AllFilters
        valueRange={valueRange}
        onValueRangeChange={onValueRangeChange}
        filters={filters}
        onFiltersChange={onFiltersChange}
        filterGroups={filterGroups}
        categories={categories}
        categoryOptions={categoryOptions}
        onCategoriesChange={onCategoriesChange}
      />
      <ValueFilter
        valueRange={valueRange}
        onValueRangeChange={onValueRangeChange}
      />

      <FilterGroups
        filters={filters}
        onFiltersChange={onFiltersChange}
        filterGroups={filterGroups}
      />

      {!!categoryOptions?.length && (
        <CategoriesFilter
          categories={categories}
          categoryOptions={categoryOptions}
          onCategoriesChange={onCategoriesChange}
        />
      )}

      {allowClear && <ClearFilters onClear={handleClear} />}
    </Flex>
  );
};

interface ValueFilterProps
  extends Pick<DesktopFiltersProps, 'valueRange' | 'onValueRangeChange'> {}
const ValueFilter: FC<ValueFilterProps> = ({
  valueRange,
  onValueRangeChange,
}) => {
  const { isOpen, onClose, onToggle } = useDisclosure();

  const isActive = !isEqual(valueRange, ANY_VALUE_RANGE);

  return (
    <Box zIndex="20">
      <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start">
        <PopoverAnchor>
          <Box>
            <FilterBox
              isActive={isActive}
              onClear={() => onValueRangeChange?.(ANY_VALUE_RANGE)}
              onClick={onToggle}
            >
              Value
            </FilterBox>
          </Box>
        </PopoverAnchor>
        <PopoverContent>
          <PopoverArrow />
          <PopoverBody w="12.5rem">
            <ValueRangeSlider
              range={valueRange ?? ANY_VALUE_RANGE}
              onRangeChange={(range) => onValueRangeChange?.(range)}
              rangeMin={VALUE_RANGE_MIN}
              rangeMax={VALUE_RANGE_MAX}
              currency={IProductCurrency.usd}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

interface CategoriesFilterProps
  extends Pick<
    DesktopFiltersProps,
    'categories' | 'categoryOptions' | 'onCategoriesChange'
  > {}
const CategoriesFilter: FC<CategoriesFilterProps> = ({
  categories = [],
  categoryOptions = [],
  onCategoriesChange,
}) => {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const count = categories?.length ?? 0;
  const isActive = !!count;

  const handleChange = useCallback(
    (selectedIds: string[]) => {
      onCategoriesChange?.(
        categoryOptions.filter(
          ({ id }) =>
            !!selectedIds.find((selectedId) => selectedId === id.toString())
        )
      );
    },
    [categoryOptions, onCategoriesChange]
  );

  return (
    <Box zIndex="20">
      <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start">
        <PopoverAnchor>
          <Box>
            <FilterBox
              isActive={isActive}
              onClick={onOpen}
              onClear={() => onCategoriesChange?.([])}
            >
              <Text>
                <Text as="span" textTransform="capitalize">
                  Category
                </Text>{' '}
                {!!count && <Text as="span">({count})</Text>}
              </Text>
            </FilterBox>
          </Box>
        </PopoverAnchor>
        <PopoverContent>
          <PopoverArrow />
          <PopoverBody>
            <CheckboxGroup
              value={categories.map(({ id }) => id.toString())}
              onChange={handleChange}
            >
              <Flex flexDir="column" gap="0.75rem" userSelect="none">
                {categoryOptions.map(({ id, title }) => (
                  <Checkbox value={id.toString()} key={id}>
                    <Text
                      color="primary.300"
                      cursor="pointer"
                      userSelect="none"
                      fontSize="0.9375rem"
                      fontWeight="300"
                      lineHeight="1.25rem"
                    >
                      {title}
                    </Text>
                  </Checkbox>
                ))}
              </Flex>
            </CheckboxGroup>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

export default DesktopFilters;
